import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, json } from './api';
import type { Allocation, FundTotal } from './types';

export function AllocationPanel() {
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [fundTotals, setFundTotals] = useState<FundTotal[]>([]);
  const [fund, setFund] = useState('');
  const [strategy, setStrategy] = useState('');
  const [amount, setAmount] = useState('');
  const [fundTotal, setFundTotal] = useState('');
  const [currency, setCurrency] = useState('USD');

  const load = useCallback(async () => {
    const [allocationBody, totalBody] = await Promise.all([
      api<{ allocations: Allocation[] }>('/api/allocations'),
      api<{ fund_totals: FundTotal[] }>('/api/fund-totals'),
    ]);
    setAllocations(allocationBody.allocations);
    setFundTotals(totalBody.fund_totals);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () =>
      Object.entries(
        allocations
          .filter((item) => item.status === 'active')
          .reduce<Record<string, number>>((result, item) => {
            const key = `${item.fund_name} · ${item.currency}`;
            result[key] = (result[key] ?? 0) + Number(item.allocated_amount);
            return result;
          }, {}),
      ),
    [allocations],
  );
  const mixedCurrencies = useMemo(() => {
    const byFund: Record<string, Set<string>> = {};
    for (const item of allocations.filter((allocation) => allocation.status === 'active')) {
      (byFund[item.fund_name] ??= new Set()).add(item.currency);
    }
    return Object.values(byFund).some((values) => values.size > 1);
  }, [allocations]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const effective_date = new Date().toISOString().slice(0, 10);
    await api('/api/allocations', {
      method: 'POST',
      body: json({ fund_name: fund, strategy_name: strategy, allocated_amount: amount, currency, effective_date }),
    });
    if (fundTotal)
      await api('/api/fund-totals', {
        method: 'POST',
        body: json({ fund_name: fund, total_amount: fundTotal, currency, effective_date }),
      });
    setAmount('');
    await load();
  };

  return (
    <div className="allocation-panel">
      <div className="drawer-header">
        <div>
          <h2>Strategy Allocation</h2>
          <p>Informational records only. Values never modify execution capital or risk limits.</p>
        </div>
      </div>
      <div className="allocation-layout">
        <form className="allocation-form" onSubmit={submit}>
          <h3>Add allocation note</h3>
          <label>
            Fund / capital pool
            <input value={fund} onChange={(event) => setFund(event.target.value)} required />
          </label>
          <label>
            Strategy
            <input value={strategy} onChange={(event) => setStrategy(event.target.value)} required />
          </label>
          <div className="amount-row">
            <label>
              Amount
              <input
                type="number"
                min="0"
                step="any"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                required
              />
            </label>
            <label>
              Currency
              <input
                value={currency}
                onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                maxLength={3}
                required
              />
            </label>
          </div>
          <label>
            Optional fund total
            <input
              type="number"
              min="0"
              step="any"
              value={fundTotal}
              onChange={(event) => setFundTotal(event.target.value)}
              placeholder="For unallocated / over-allocation"
            />
          </label>
          <button className="primary">Record allocation</button>
        </form>
        <div className="allocation-table">
          {mixedCurrencies && (
            <p className="allocation-warning">
              Mixed currencies detected for one or more funds; totals are not converted.
            </p>
          )}
          <div className="total-cards">
            {totals.map(([name, value]) => {
              const [fundName, code] = name.split(' · ');
              const capacity = fundTotals.find((item) => item.fund_name === fundName && item.currency === code);
              const remaining = capacity ? Number(capacity.total_amount) - value : null;
              return (
                <div className={remaining != null && remaining < 0 ? 'overallocated' : ''} key={name}>
                  <small>{name}</small>
                  <strong>{value.toLocaleString()}</strong>
                  {remaining != null && (
                    <em>
                      {remaining < 0
                        ? `${Math.abs(remaining).toLocaleString()} over-allocated`
                        : `${remaining.toLocaleString()} unallocated`}
                    </em>
                  )}
                </div>
              );
            })}
          </div>
          <table>
            <thead>
              <tr>
                <th>Fund</th>
                <th>Strategy</th>
                <th>Amount</th>
                <th>Effective</th>
                <th>Owner</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {allocations.map((item) => (
                <tr key={item.id} className={item.status === 'archived' ? 'archived' : ''}>
                  <td>{item.fund_name}</td>
                  <td>{item.strategy_name}</td>
                  <td>
                    {Number(item.allocated_amount).toLocaleString()} {item.currency}
                  </td>
                  <td>{item.effective_date}</td>
                  <td>{item.owner_id}</td>
                  <td>
                    <button
                      onClick={async () => {
                        await api(`/api/allocations/${item.id}`, {
                          method: 'PATCH',
                          body: json({ status: item.status === 'active' ? 'archived' : 'active' }),
                        });
                        await load();
                      }}
                    >
                      {item.status === 'active' ? 'Archive' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
